import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCartStore } from '../stores/cartStore';
import { toast } from 'sonner';
import { AddToCartModal } from "./AddToCartModal";
import React, { useState } from "react";

export function ProductCard({ product, flashSale = null }) {
  const addItem = useCartStore((state) => state.addItem);
  const [showModal, setShowModal] = useState(false);

  const displayPrice = flashSale
    ? product.price * (1 - flashSale.discount_percent / 100)
    : product.price;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const defaultSize = product.sizes?.[0] || null;
    const defaultColor = product.colors?.[0] || null;

    addItem({
      id: product.id,
      name: product.name,
      price: displayPrice,
      original_price: product.original_price,
      image: product.images?.[0] || '',
      size: defaultSize,
      color: defaultColor,
    });

    toast.success(
      `${product.name} (${defaultSize || "No size"} ${defaultColor || "No color"}) added to cart!`
    );
    setShowModal(true);
  };

  const discountPercent = product.original_price
    ? Math.round(
      ((product.original_price - product.price) / product.original_price) * 100
    )
    : 0;

  return (
    <>
      <div className="relative bg-white border border-stone-200 overflow-hidden product-card group">
        {/* Badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
          {flashSale && (
            <Badge className="bg-red-600 text-white animate-pulse-sale">
              -{flashSale.discount_percent}% OFF
            </Badge>
          )}
          {product.original_price && !flashSale && (
            <Badge className="bg-emerald-600 text-white">{discountPercent}% OFF</Badge>
          )}
          {product.featured && (
            <Badge variant="outline" className="bg-white">Featured</Badge>
          )}
        </div>

        {/* Image */}
        <Link to={`/products/${product.id}`}>
          <div className="aspect-square bg-stone-100 overflow-hidden">
            <img
              src={product.images?.[0] || 'https://via.placeholder.com/400'}
              alt={product.name}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        </Link>

        {/* Content */}
        <div className="p-4">
          <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">
            {product.category_name}
          </p>

          <Link to={`/products/${product.id}`}>
            <h3 className="font-semibold text-stone-900 mb-2 line-clamp-2 hover:text-emerald-900">
              {product.name}
            </h3>
          </Link>

          {/* Price + Buttons on same line */}
          {/* Price */}
          <div className="flex items-center gap-2">
            <span className="text-base md:text-lg font-bold text-emerald-900 leading-tight">
              KES {displayPrice.toLocaleString()}
            </span>

            {(product.original_price || flashSale) && (
              <span className="text-sm text-stone-400 line-through">
                KES {product.original_price.toLocaleString()}
              </span>
            )}
          </div>

          {/* Button */}
          <div className="flex mt-2">
            <Button
              size="sm"
              className="w-full bg-emerald-900 hover:bg-emerald-800"
              onClick={handleAddToCart}
            >
              <ShoppingCart className="h-4 w-4 mr-1" />
              Add To Cart
            </Button>

            {product.category_name === "Cakes" ? (
              <Link key={product.id} to={`/customize/${product.id}`}>
                <Button size="sm" variant="secondary">
                  Customize
                </Button>
              </Link>
            ) : (
              <Link key={product.id} to={`/products/${product.id}`}>
                <Button size="sm" variant="secondary">
                  View
                </Button>
              </Link>
            )}
            {/* 
            <Button size="sm" variant="secondary">
              <Eye className="h-4 w-4" />
            </Button> */}
          </div>

          {/* Stock info */}
          {product.stock <= 5 && product.stock > 0 && (
            <p className="text-xs text-red-600">Only {product.stock} left!</p>
          )}
          {product.stock === 0 && (
            <p className="text-xs text-stone-500">Out of stock</p>
          )}
        </div>
      </div>

      <AddToCartModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        product={product}
      />
    </>
  );
}


